'use client';

import * as React from 'react';
import { useMemo, useState } from 'react';
import { Icon } from '@/components/entities/Sprite/Icon';
import { cn } from '@/utils/cn';
import { Label } from '../Label/label';

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 'sm' | 'lg';
  error?: { isError: boolean; errorText?: string };
  icon?: React.ReactNode;
  reverseContent?: boolean;
  disabled?: boolean;
  label?: string;
  labelClassName?: string;
  containerClassName?: string;
  iconWrapperClassName?: string;
  clearSearch?: () => void;
  value?: string;
  ref?: React.Ref<HTMLInputElement>;
};

const Input = ({ ref, className, type, size = 'sm', placeholder, error, icon, reverseContent = false, disabled = false, label, id, labelClassName, containerClassName, iconWrapperClassName, clearSearch, value, ...props }: InputProps) => {
  const defaultError = useMemo(() => ({ isError: false, errorText: '' }), []);
  const errorState = error ?? defaultError;
  const sizeClasses = {
    sm: 'py-2.5 text-base font-normal',
    lg: 'py-3.5 text-base font-normal',
  };

  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';

  return (
    <div className={cn('gap-2 flex flex-col', containerClassName)}>
      {label && (
        <Label className={cn('text-white', labelClassName)} htmlFor={id}>
          {label}
        </Label>
      )}

      <div className={cn('relative flex items-center w-full')}>
        {icon && (
          clearSearch
            ? (
                <button
                  type="button"
                  aria-label="Clear"
                  onClick={() => clearSearch()}
                  className={cn(
                    'absolute size-6 top-1/2 transform -translate-y-1/2 z-20',
                    reverseContent ? 'right-4' : 'left-4',
                    'text-xl pointer-events-auto cursor-pointer flex items-center justify-center',
                    iconWrapperClassName,
                  )}
                >
                  {icon}
                </button>
              )
            : (
                <div
                  className={cn(
                    'absolute size-6 top-1/2 transform -translate-y-1/2',
                    reverseContent ? 'right-4' : 'left-4',
                    'text-xl pointer-events-none flex items-center justify-center',
                    iconWrapperClassName,
                  )}
                >
                  {icon}
                </div>
              )
        )}

        <input
          id={id}
          type={isPasswordField && showPassword ? 'text' : type}
          placeholder={placeholder}
          data-slot="input"
          disabled={disabled}
          value={value}
          className={
            cn(
              'rounded-[6.25rem] caret-primary-2 w-full px-4 outline bg-c900 text-white placeholder:text-primary-4-light transition-colors duration-300',
              (icon && !reverseContent) && 'pl-10',
              (icon && reverseContent) || isPasswordField ? 'pr-10' : '',
              !disabled && !errorState.isError && '-outline-offset-1 outline-primary-4-dark focus:outline-2 focus:outline-primary-2 hover:outline-2 hover:outline-primary-2',
              errorState.isError && '-outline-offset-1  outline-main-error focus:outline-main-error',
              disabled && 'text-violet-main-700 cursor-not-allowed outline-none',
              sizeClasses[size],
              className,
            )
          }
          ref={ref}
          {...props}
        />

        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(prev => !prev)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-c300 cursor-pointer"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {!showPassword
              ? (
                  <Icon name="eye" size={24} />
                )
              : (
                  <Icon name="eye-slash" size={24} />
                )}
          </button>
        )}
      </div>

      {errorState.isError && errorState.errorText && (
        <p className="text-main-error paragraph-s-regular">
          {errorState.errorText}
        </p>
      )}
    </div>
  );
};

Input.displayName = 'Input';

export { Input };
